import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Cart() {
  const { items, updateQuantity, removeItem, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shipping, setShipping] = useState({ name: user?.name || "", address: "" });

  const shippingFee = total >= 50 || total === 0 ? 0 : 5.99;
  const grand = total + shippingFee;

  const handleCheckout = async () => {
    if (!user) { toast.error("Please sign in to checkout"); navigate("/login"); return; }
    if (!shipping.name.trim() || !shipping.address.trim()) { toast.error("Please fill in shipping details"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping_name: shipping.name,
        shipping_address: shipping.address,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="empty-cart">
        <div className="w-20 h-20 mx-auto rounded-full border-2 border-ink bg-brand-yellow flex items-center justify-center mb-6 shadow-hard">
          <ShoppingBag className="w-9 h-9" />
        </div>
        <h1 className="text-3xl font-black mb-3">Your cart is empty</h1>
        <p className="text-muted-foreground mb-8">Time to snag some good stuff!</p>
        <Link to="/shop" className="inline-flex px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all" data-testid="shop-now-button">
          Start Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10" data-testid="cart-page">
      <h1 className="text-4xl font-black tracking-tighter mb-8">Your Cart ({items.length})</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div key={item.id} className="flex gap-4 bg-white rounded-3xl border-2 border-ink shadow-hard-sm p-4" data-testid={`cart-item-${item.id}`}>
              <img src={item.image} alt={item.title} className="w-24 h-24 rounded-2xl object-cover border-2 border-ink" />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold font-heading leading-tight mb-1 truncate">{item.title}</h3>
                <p className="text-lg font-black mb-2">${item.price.toFixed(2)}</p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border-2 border-ink rounded-full overflow-hidden">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-secondary" data-testid={`decrease-${item.id}`}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-8 text-center font-bold text-sm">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-secondary" data-testid={`increase-${item.id}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => removeItem(item.id)} className="text-brand-pink hover:text-red-600 flex items-center gap-1 text-sm font-bold" data-testid={`remove-${item.id}`}>
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                </div>
              </div>
              <div className="font-black text-lg shrink-0">${(item.price * item.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-3xl border-2 border-ink shadow-hard p-6 sticky top-24">
            <h2 className="text-xl font-black mb-5">Order Summary</h2>

            <div className="space-y-3 mb-5">
              <input value={shipping.name} onChange={(e) => setShipping({ ...shipping, name: e.target.value })}
                placeholder="Full name" className="w-full bg-white border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium" data-testid="shipping-name-input" />
              <textarea value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })}
                placeholder="Shipping address" rows={2} className="w-full bg-white border-2 border-ink rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-brand/20 font-medium resize-none" data-testid="shipping-address-input" />
            </div>

            <div className="space-y-2 text-sm font-medium border-t-2 border-dashed border-ink pt-4">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">${total.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{shippingFee === 0 ? <span className="text-brand-green font-bold">FREE</span> : `$${shippingFee.toFixed(2)}`}</span></div>
              <div className="flex justify-between text-xl font-black pt-2 border-t-2 border-ink mt-2"><span>Total</span><span data-testid="cart-total">${grand.toFixed(2)}</span></div>
            </div>

            <button onClick={handleCheckout} disabled={loading}
              className="w-full mt-6 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold bg-brand text-white border-2 border-ink shadow-hard-btn hover:translate-y-[3px] hover:shadow-none transition-all disabled:opacity-70" data-testid="checkout-button">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting...</> : <><Lock className="w-4 h-4" /> Checkout Securely</>}
            </button>
            <p className="text-xs text-center text-muted-foreground mt-3">Powered by Stripe · Test mode</p>
          </div>
        </div>
      </div>
    </div>
  );
}
